import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { usePlayersStore } from '../lib/store';
import styles from './Players.module.css';

export default function Players() {
	const { players, isLoading, error, fetchPlayers } = usePlayersStore();
	const [search, setSearch] = useState('');

	useEffect(() => {
		fetchPlayers();
	}, [fetchPlayers]);

	const filteredPlayers = players.filter((player) => player.externalId.toLowerCase().includes(search.toLowerCase()) || player.id.includes(search));

	if (isLoading && !players.length) {
		return (
			<div className={styles.loading}>
				<div className={styles.spinner} />
				<span>Loading players...</span>
			</div>
		);
	}

	return (
		<div className={styles.players}>
			<header className={styles.header}>
				<div>
					<h1 className={styles.title}>Players</h1>
					<p className={styles.subtitle}>View and manage your players</p>
				</div>
			</header>

			{error && <div className={styles.error}>{error}</div>}

			{/* Search */}
			<div className={styles.searchBox}>
				<SearchIcon />
				<input type="text" placeholder="Search by player ID..." value={search} onChange={(e) => setSearch(e.target.value)} className={styles.searchInput} />
			</div>

			{/* Players Table */}
			<div className={styles.tableCard}>
				<table className={styles.table}>
					<thead>
						<tr>
							<th>Player ID</th>
							<th>Currency</th>
							<th>Total Bets</th>
							<th>Wagered</th>
							<th>Won</th>
							<th>Net Result</th>
							<th>Last Active</th>
							<th></th>
						</tr>
					</thead>
					<tbody>
						{filteredPlayers.length ? (
							filteredPlayers.map((player) => (
								<tr key={player.id}>
									<td className={styles.playerId}>{player.externalId}</td>
									<td>{player.currency}</td>
									<td>{player.totalBets.toLocaleString()}</td>
									<td>{player.totalWagered}</td>
									<td>{player.totalWon}</td>
									<td className={player.netResult.startsWith('-') ? styles.negative : styles.positive}>{player.netResult}</td>
									<td className={styles.timestamp}>{formatTime(player.lastActive)}</td>
									<td>
										<Link to={`/players/${player.id}`} className={styles.viewBtn}>
											View
										</Link>
									</td>
								</tr>
							))
						) : (
							<tr>
								<td colSpan={8} className={styles.emptyState}>
									{search ? 'No players found matching your search' : 'No players yet'}
								</td>
							</tr>
						)}
					</tbody>
				</table>
			</div>

			{/* Stats Summary */}
			<div className={styles.summary}>
				<span className={styles.summaryItem}>
					<strong>{players.length}</strong> total players
				</span>
				<span className={styles.summaryItem}>
					<strong>{players.reduce((sum, p) => sum + p.totalBets, 0).toLocaleString()}</strong> total bets
				</span>
			</div>
		</div>
	);
}

function formatTime(timestamp: number): string {
	const date = new Date(timestamp);
	const now = new Date();
	const diffMs = now.getTime() - date.getTime();
	const diffMins = Math.floor(diffMs / 60000);

	if (diffMins < 1) return 'Just now';
	if (diffMins < 60) return `${diffMins}m ago`;

	const diffHours = Math.floor(diffMins / 60);
	if (diffHours < 24) return `${diffHours}h ago`;

	const diffDays = Math.floor(diffHours / 24);
	if (diffDays < 7) return `${diffDays}d ago`;

	return date.toLocaleDateString();
}

function SearchIcon() {
	return (
		<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
			<circle cx="11" cy="11" r="8" />
			<line x1="21" y1="21" x2="16.65" y2="16.65" />
		</svg>
	);
}
